import { useMemo, useState } from "react";
import { useGetSubscriptionsQuery, useGetLicensesQuery } from "../../store/services/details-api";
import PaymentManagementItem from "./PaymentManagementItem";
import LoaderCard from "../../components/Loader/LoaderCard";
import { getDecodedAccessToken } from "../../utils";
import { subscriptionData } from "../../types/details-api-types";
import Modal from "../../components/modal/modal";
import ManageSubscriptionModal from "../../components/modals/ManageSubscriptionModal/ManageSubscription";
import RenewalModal from "../../components/modals/RenewalModal/RenewalModal";
import SuccessfullyRenewalModal from "../../components/modals/SuccessfullyRenewalModal/SuccessfullyRenewalModal";
import ProcessingModal from "../../components/modals/ProcessingModal/ProcessingModal";
import PauseSubscriptionModal from "../../components/modals/PauseSubscriptionModal/PauseSubscriptionModal";
import SuccessfullyModal from "../../components/modals/SuccessfullyModal/SuccessfullyModal";
import { useTranslation } from "react-i18next";
import UpdatePaymentModal from "../../components/modals/UpdatePaymentModal/UpdatePaymentModal";
import CancelSubscriptionModal from "../../components/modals/CancelSubscriptionModal/CancelSubscriptionModal";
import CancelSubscriptionOfferModal from "../../components/modals/CancelSubscriptionOfferModal/CancelSubscriptionOfferModal";
import SendCodeModal from "../../components/modals/SendCodeModal/SendCodeModal";
import CreditCardInfoModal from "../../components/modals/CreditCardInfoModal/CreditCardInfoModal";
import PayPalModal from "../../components/modals/PayPalModal/PayPalModal";
import UpdatePasswordModal from "../../components/modals/UpdatePasswordModal/UpdatePasswordModal";
import styles from "../../components/MenuItemHeader/MenuItemHeader.module.scss";
import SurveyModal from "../../components/modals/SurveyModal/SurveyModal";
import CancelSubscriptionOfferV2Modal from "../../components/modals/CancelSubscriptionOfferV2Modal/CancelSubscriptionOfferV2Modal";
import { useAppSelector } from "../../store/hooks";

const PaymentManagement = () => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const userData = getDecodedAccessToken();
  const userEmail = useAppSelector((state) => state.auth.user?.email); // email-code tokens carry no email
  const [isModalUpdatePayVisible, setIsModalUpdatePayVisible] = useState(false);

  const [selectedSubscription, setSelectedSubscription] = useState<subscriptionData | null>(null);
  const [isModalManageSubVisible, setIsModalManageSubVisible] = useState(false);
  const [isModalPauseSubVisible, setIsModalPauseSubVisible] = useState(false);
  const [isModalCancelSubVisible, setIsCancelSubVisible] = useState(false);
  const [isModalRenewalVisible, setIsModalRenewalVisible] = useState(false);
  const [isModalProcessingVisible, setIsModalProcessingVisible] = useState(false);
  const [isModalSuccessfullyVisible, setIsModalSuccessfullyVisible] = useState(false);
  const [isModalPauseConfirmVisible, setIsModalPauseConfirmVisible] = useState(false);
  const [isModalUpdatePaySuccessfullyVisible, setIsModalUpdatePaySuccessfullyVisible] = useState(false);
  const [isModalCancelSubOfferVisible, setIsModalCancelSubOfferVisible] = useState(false);
  const [isModalCancelSubOfferV2Visible, setIsModalCancelSubOfferV2Visible] = useState(false);
  const [isCancellationConfirmation, setIsCancellationConfirmation] = useState(false);
  const [isModalCancelSubProcesVisible, setIsCancelSubProcesVisible] = useState(false);
  const [isModalCancelSubSuccessfulyVisible, setIsCancelSubSuccessfulyVisible] = useState(false);
  const [isModalSendLinkVisible, setIsModalSendLinkVisible] = useState(false);
  const [isModalCardInfoVisible, setIsModalCardInfoVisible] = useState(false);
  const [isModalPayPalVisible, setIsUpdatePayPalVisible] = useState(false);
  const [isModalNewPasswordVisible, setIsModalNewPasswordVisible] = useState(false);
  const [isSurveyModalVisible, setIsSurveyModalVisible] = useState(false);

  const confirmationModalData = useMemo(() => {
    const data = {
      subTitle: t("Please check your inbox for an email containing a magic link to reset your password."),
    };

    if (isCancellationConfirmation) {
      data.subTitle = t("For safety measures, we sent a 6-digit code to your email.");
    }

    return data;
  }, [isCancellationConfirmation, t]);

  const { data: subscriptions, refetch, isFetching: subsIsFetching } = useGetSubscriptionsQuery(null);
  const { data: licenses, refetch: licenseRefetch, isFetching: licenseIsFetching } = useGetLicensesQuery(null);
  const isFetching = subsIsFetching || licenseIsFetching;

  const RefreshData = async () => {
    setLoading(true);
    setTimeout(async () => {
      await Promise.all([refetch(), licenseRefetch()]);
      setLoading(false);
    }, 6500);
  };
  const data = useMemo(() => {
    if (!subscriptions || !licenses) return [];

    return subscriptions
      .map((d) => ({
        ...d,
        licenses: licenses.filter((item) => item.subscription_id === d.subscription_id),
        isExpired: licenses
          .filter((license) => license.subscription_id === d.subscription_id)
          .every((license) => new Date(license.expiration_date).getTime() < new Date().getTime()),
      }))
      .sort((a: { isExpired: any }, b: { isExpired: any }) => Number(a.isExpired) - Number(b.isExpired));
  }, [licenses, subscriptions]);

  return (
    <>
      <h3 className={styles.root_subtitle} style={{ marginTop: "35px" }}>
        {t("Payment Management")}
      </h3>
      {!loading && !isFetching && data ? (
        data!.map((card, index) => (
          <div key={index}>
            <PaymentManagementItem
              cardData={card}
              subNumber={card.subscription_id}
              price={card.renewalPrice}
              devicesNumber={card.license.seats}
              devicesInUse={card.license.seatsUsed}
              priceCurrency={card.renewalPriceCurrency}
              email={userData!.email ?? userEmail}
              method={card.invoices[0].paymentType}
              setIsModalUpdatePayVisible={setIsModalUpdatePayVisible}
              openModal={() => {
                setSelectedSubscription(card);
                setIsModalManageSubVisible(true);
              }}
              refreshData={RefreshData}
              isExpired={card.isExpired}
            />
          </div>
        ))
      ) : (
        <LoaderCard />
      )}

      {/* Manage Subscription Modal */}
      <Modal isOpen={isModalManageSubVisible} setIsVisible={() => setIsModalManageSubVisible(false)}>
        <ManageSubscriptionModal
          setIsModalManageSubVisible={setIsModalManageSubVisible}
          setIsModalPauseSubVisible={setIsModalPauseSubVisible}
          setIsModalUpdatePayVisible={setIsModalUpdatePayVisible}
          setIsCancelSubVisible={setIsCancelSubVisible}
          setIsModalRenewalVisible={setIsModalRenewalVisible}
          subscriptionItem={selectedSubscription}
        />
      </Modal>

      {/* Renewal Modal */}
      <Modal isOpen={isModalRenewalVisible} setIsVisible={() => setIsModalRenewalVisible(false)}>
        <RenewalModal
          setIsModalPauseConfirmVisible={setIsModalRenewalVisible}
          setIsModalProcessingVisible={setIsModalProcessingVisible}
          setIsModalSuccessfullyVisible={setIsModalSuccessfullyVisible}
          data={selectedSubscription as subscriptionData}
        />
      </Modal>

      {/* Successfully Renewal Modal */}
      <Modal isOpen={isModalSuccessfullyVisible} setIsVisible={() => setIsModalSuccessfullyVisible(false)}>
        <SuccessfullyRenewalModal
          setIsModalPauseConfirmVisible={() => setIsModalSuccessfullyVisible(false)}
          refreshData={() => RefreshData()}
          data={selectedSubscription as subscriptionData}
        />
      </Modal>

      {/* Processing Modal */}
      <Modal
        isOpen={isModalProcessingVisible}
        setIsVisible={() => {
          setIsModalProcessingVisible(false);
        }}
      >
        <ProcessingModal setIsModalProcessingVisible={setIsModalProcessingVisible} />
      </Modal>

      {/* Pause Subscription Modal */}
      <Modal isOpen={isModalPauseSubVisible} setIsVisible={() => setIsModalPauseSubVisible(false)}>
        <PauseSubscriptionModal
          setIsModalPauseSubVisible={setIsModalPauseSubVisible}
          setIsModalPauseConfirmVisible={setIsModalPauseConfirmVisible}
          subscriptionItem={selectedSubscription}
          refetch={RefreshData}
        />
      </Modal>

      {/* Pause Subscription Confirmation Modal */}
      <Modal isOpen={isModalPauseConfirmVisible} setIsVisible={() => setIsModalPauseConfirmVisible(false)}>
        <SuccessfullyModal
          setIsModalPauseConfirmVisible={setIsModalPauseConfirmVisible}
          tittle={t("Subscription Paused")}
          subtitle={t(
            "Your Fortect subscription has been paused. You will still have access to all features until the end of your current billing period. We've sent a confirmation email to your registered email address."
          )}
        />
      </Modal>

      {/* Update Payment Modal */}
      <Modal isOpen={isModalUpdatePayVisible} setIsVisible={() => setIsModalUpdatePayVisible(false)}>
        <UpdatePaymentModal
          setIsModalUpdatePayVisible={setIsModalUpdatePayVisible}
          setIsModalCardInfoVisible={setIsModalCardInfoVisible}
          setIsUpdatePayPalVisible={setIsUpdatePayPalVisible}
          setIsModalUpdatePaySuccessfullyVisible={setIsModalUpdatePaySuccessfullyVisible}
          subscriptionItem={selectedSubscription}
        />
      </Modal>

      {/* Survey Modal */}
      <Modal isOpen={isSurveyModalVisible} setIsVisible={() => setIsSurveyModalVisible(false)}>
        <SurveyModal
          callbackAction={(action) => {
            setIsSurveyModalVisible(false);
            const isOffered = sessionStorage.getItem("offered");
            if(action === "offer" && !isOffered) {
              setIsModalCancelSubOfferV2Visible(true);
            } else if (action === "deactivate" || action === "offer") {
              setIsCancellationConfirmation(true);
              setIsModalSendLinkVisible(true);
            } else if (action === "back") {
              setIsCancelSubVisible(true);
            }
          }}
        />
      </Modal>

      {/* Cancel Subscription Modal */}
      <Modal isOpen={isModalCancelSubVisible} setIsVisible={() => setIsCancelSubVisible(false)}>
        <CancelSubscriptionModal
          setIsModalPauseSubVisible={setIsModalPauseSubVisible}
          setIsCancelSubVisible={setIsCancelSubVisible}
          subscriptionItem={selectedSubscription as subscriptionData}
          callbackAction={(action, data) => {
            if (action === "deactivate") {
              setSelectedSubscription(data as subscriptionData);
              setIsCancellationConfirmation(true);
              // setIsModalSendLinkVisible(true);
              setIsSurveyModalVisible(true);
            } else if (action === "offer") {
              sessionStorage.setItem("offered", "true");
              setIsModalCancelSubOfferVisible(true);
              setSelectedSubscription(data as subscriptionData);
              setIsCancelSubVisible(false);
            }
          }}
        />
      </Modal>

      {/* Update Payment Successful Modal */}
      <Modal
        isOpen={isModalUpdatePaySuccessfullyVisible}
        setIsVisible={() => setIsModalUpdatePaySuccessfullyVisible(false)}
      >
        <SuccessfullyModal
          setIsModalPauseConfirmVisible={setIsModalUpdatePaySuccessfullyVisible}
          tittle={t("Payment Method Updated Successfully")}
          subtitle={t(
            "Your payment method has been successfully updated. Your Fortect subscription will now be charged using the new payment"
          )}
        />
      </Modal>

      {/* Cancel Subscription Offer Modal */}
      <Modal
        overflow
        overflowDisableClose
        isOpen={isModalCancelSubOfferVisible}
        setIsVisible={() => setIsModalCancelSubOfferVisible(false)}
      >
        <CancelSubscriptionOfferModal
          subscriptionItem={selectedSubscription as subscriptionData}
          callbackAction={(action, data) => {
            if (action === "deactivate") {
              setSelectedSubscription(data as subscriptionData);
              setIsCancellationConfirmation(true);
              // setIsModalSendLinkVisible(true);
              setIsSurveyModalVisible(true);
              setIsModalCancelSubOfferVisible(false);
            } else if (action === "close") {
              setIsModalCancelSubOfferVisible(false);
            }
          }}
        />
      </Modal>

       {/* Cancel Subscription Offer Modal */}
      <Modal
        overflow
        overflowDisableClose
        isOpen={isModalCancelSubOfferV2Visible}
        setIsVisible={() => setIsModalCancelSubOfferV2Visible(false)}
      >
        <CancelSubscriptionOfferV2Modal
          subscriptionItem={selectedSubscription as subscriptionData}
          callbackAction={(action, data) => {
            if (action === "deactivate") {
              setSelectedSubscription(data as subscriptionData);
              setIsCancellationConfirmation(true);
              setIsModalSendLinkVisible(true);
              setIsModalCancelSubOfferV2Visible(false);
            } else if (action === "close") {
              setIsModalCancelSubOfferV2Visible(false);
            }
          }}
        />
      </Modal>

      {/* Cancel Subscription Processing Modal */}
      <Modal isOpen={isModalCancelSubProcesVisible} setIsVisible={() => setIsCancelSubProcesVisible(false)}>
        <ProcessingModal setIsModalProcessingVisible={setIsCancelSubProcesVisible} />
      </Modal>

      {/* Cancel Subscription Successful Modal */}
      <Modal isOpen={isModalCancelSubSuccessfulyVisible} setIsVisible={() => setIsCancelSubSuccessfulyVisible(false)}>
        <SuccessfullyModal
          setIsModalPauseConfirmVisible={setIsCancelSubSuccessfulyVisible}
          tittle={t("Subscription Cancelled Successfully")}
          subtitle={t(
            "Your Fortect subscription has been cancelled. You will still have access to all features until the end of your current billing period. We've sent a confirmation email to your registered email address."
          )}
        />
      </Modal>

      {/* Send Code Modal */}
      <Modal isOpen={isModalSendLinkVisible} setIsVisible={() => setIsModalSendLinkVisible(false)}>
        <SendCodeModal
          title={t("Check Your Email for the 6-digit code")}
          subtitle={confirmationModalData.subTitle}
          message={t("If you don't see the email, make sure to look in your spam/junk folder.")}
          setIsModalSendLinkVisible={setIsModalSendLinkVisible}
          setIsModalNewPasswordVisible={setIsModalNewPasswordVisible}
          isForgotPasswordPage={false}
          isCancellationConfirmation={isCancellationConfirmation}
          setNextFlowModal={setIsCancelSubProcesVisible}
          subscriptionItem={selectedSubscription}
          callback={(action) => {
            RefreshData();
            if (action === "success") {
              setIsCancelSubProcesVisible(false);
              setIsCancelSubSuccessfulyVisible(true);
            } else {
              setIsCancelSubVisible(true);
              setIsCancelSubProcesVisible(false);
            }
          }}
          isCodeVerification={true}
        />
      </Modal>

      {/* Credit Card Info Modal */}
      <Modal
        isOpen={isModalCardInfoVisible}
        setIsVisible={() => {
          setIsModalCardInfoVisible(false);
        }}
      >
        <CreditCardInfoModal
          setIsModalCardInfoVisible={setIsModalCardInfoVisible}
          setIsModalUpdatePayVisible={setIsModalUpdatePayVisible}
          setIsModalProcessingVisible={setIsModalProcessingVisible}
          setIsModalUpdatePaySuccessfullyVisible={setIsModalUpdatePaySuccessfullyVisible}
        />
      </Modal>

      {/* PayPal Modal */}
      <Modal
        isOpen={isModalPayPalVisible}
        setIsVisible={() => {
          setIsUpdatePayPalVisible(false);
        }}
      >
        <PayPalModal setIsUpdatePayPalVisible={setIsUpdatePayPalVisible} />
      </Modal>

      {/* Update Password Modal */}
      <Modal isOpen={isModalNewPasswordVisible} setIsVisible={() => setIsModalNewPasswordVisible(false)}>
        <UpdatePasswordModal isForgotPasswordPage={false} setIsVisible={setIsModalNewPasswordVisible} />
      </Modal>
    </>
  );
};

export default PaymentManagement;
